import React from 'react';

/**
 * Hur många px av fönstrets nederkant som tangentbordet täcker. På iOS (Safari och appens WKWebView)
 * krymper bara den visuella vyn när tangentbordet visas, så fasta element längst ner hamnar under det.
 */
export function keyboardInset(innerHeight: number, vv: { height: number; offsetTop: number } | null | undefined): number {
  if (!vv) return 0;
  const inset = innerHeight - vv.height - vv.offsetTop;
  // Under ~80 px är det adressfältet som rör sig, inte ett tangentbord.
  return inset > 80 ? Math.round(inset) : 0;
}

/** Hur många px ett element täcker av fönstrets nederkant (0 om det inte syns). */
export function coverFromRect(innerHeight: number, rect: { top: number; height: number } | null | undefined): number {
  if (!rect || rect.height <= 0) return 0;
  return Math.max(0, Math.round(innerHeight - rect.top));
}

/**
 * UX9: hur långt sidan ska skrollas för att sista meddelandet (dess underkant `endBottom`) ska synas ovanför
 * skrivfältet (`composerTop`), båda i fönstrets koordinater. 0 när det redan syns: då skrollas inget alls,
 * så en kort chatt står still med markens rubrik överst.
 */
export function scrollToShowEnd(endBottom: number, composerTop: number, margin = 12): number {
  return Math.max(0, Math.ceil(endBottom - (composerTop - margin)));
}

/**
 * Avstånd från fönstrets nederkant som ett fast skrivfält måste hålla: tangentbordet eller ett
 * bottenkort (`coverSelector`, t.ex. jaktlägets kort), det som är störst. `active` styr när det mäts
 * (bottenkortet ska finnas i DOM:en när mätningen startar).
 */
export function useBottomInset(coverSelector?: string, active = true): number {
  const [inset, setInset] = React.useState(0);
  React.useEffect(() => {
    if (!active) { setInset(0); return; }
    const vv = window.visualViewport;
    let raf = 0;
    const measure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const kb = keyboardInset(window.innerHeight, vv);
        const el = coverSelector ? document.querySelector<HTMLElement>(coverSelector) : null;
        const cover = el ? coverFromRect(window.innerHeight, el.getBoundingClientRect()) : 0;
        setInset(Math.max(kb, cover));
      });
    };
    measure();
    vv?.addEventListener('resize', measure);
    vv?.addEventListener('scroll', measure);
    window.addEventListener('resize', measure);
    // Bottenkortet fälls ihop med transform, så lyssna på stiländringar och övergångar också.
    const el = coverSelector ? document.querySelector<HTMLElement>(coverSelector) : null;
    const ro = el && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    const mo = el ? new MutationObserver(measure) : null;
    if (el) { ro?.observe(el); mo?.observe(el, { attributes: true, attributeFilter: ['style'] }); el.addEventListener('transitionend', measure); }
    return () => {
      cancelAnimationFrame(raf);
      vv?.removeEventListener('resize', measure);
      vv?.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      ro?.disconnect(); mo?.disconnect();
      el?.removeEventListener('transitionend', measure);
    };
  }, [coverSelector, active]);
  return inset;
}
